/**
 * Hook para gerenciamento da Galeria de Mídia
 * 
 * Fornece operações CRUD para arquivos de mídia do tenant:
 * - Upload com validação de tipo e tamanho
 * - Listagem com filtros
 * - Exclusão de arquivos
 * - Cálculo de uso total
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { getStorageUrl } from '@/lib/storage';
import {
  MediaGalleryItem,
  MediaGalleryFilters,
  MediaUploadResult,
  FileValidationResult,
  MediaGalleryUsage,
  MediaFileType,
  MEDIA_SIZE_LIMITS,
  TENANT_STORAGE_LIMIT,
  ACCEPTED_MIME_TYPES,
  getFileTypeFromMime,
  validateFileSize,
  formatBytes,
} from '@/types/media';

const logger = createLogger('useMediaGallery');

/**
 * Hook principal para operações com galeria de mídia
 */
export function useMediaGallery(filters?: MediaGalleryFilters) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const tenantId = currentTenant?.id;

  // =====================================================
  // QUERY: Buscar arquivos da galeria
  // =====================================================
  const {
    data: mediaList = [],
    isLoading: isLoadingMedia,
    error: mediaError,
    refetch: refetchMedia,
  } = useQuery({
    queryKey: ['media-gallery', tenantId, filters],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('Tenant não identificado');
      }

      logger.debug('Buscando mídia da galeria', { tenantId, filters });

      let query = supabase
        .from('media_gallery')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      // Aplicar filtros
      if (filters?.file_type && filters.file_type !== 'all') {
        query = query.eq('file_type', filters.file_type);
      }

      if (filters?.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      if (filters?.uploaded_by) {
        query = query.eq('uploaded_by', filters.uploaded_by);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from.toISOString());
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Erro ao buscar mídia', error);
        throw error;
      }

      logger.info('Mídia carregada', { count: data?.length || 0 });
      return (data || []) as MediaGalleryItem[];
    },
    enabled: !!tenantId,
    staleTime: 30000, // 30 segundos
  });

  // =====================================================
  // QUERY: Calcular uso total do tenant
  // =====================================================
  const {
    data: usage,
    isLoading: isLoadingUsage,
    refetch: refetchUsage,
  } = useQuery({
    queryKey: ['media-gallery-usage', tenantId],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('Tenant não identificado');
      }

      logger.debug('Calculando uso da galeria', { tenantId });

      // Buscar estatísticas usando função SQL
      const { data: totalBytes, error: usageError } = await supabase
        .rpc('get_tenant_media_usage', { p_tenant_id: tenantId });

      if (usageError) {
        logger.error('Erro ao calcular uso total', usageError);
        throw usageError;
      }

      // Buscar contadores por tipo
      const { data: stats, error: statsError } = await supabase
        .from('media_gallery')
        .select('file_type, size_bytes')
        .eq('tenant_id', tenantId);

      if (statsError) {
        logger.error('Erro ao buscar estatísticas', statsError);
        throw statsError;
      }

      // Agregar por tipo
      const byType = {
        image: { count: 0, bytes: 0 },
        video: { count: 0, bytes: 0 },
        audio: { count: 0, bytes: 0 },
        document: { count: 0, bytes: 0 },
      };

      (stats || []).forEach((item) => {
        const type = item.file_type as MediaFileType;
        if (byType[type]) {
          byType[type].count++;
          byType[type].bytes += item.size_bytes;
        }
      });

      const total = Number(totalBytes) || 0;
      const usageData: MediaGalleryUsage = {
        total_bytes: total,
        total_files: stats?.length || 0,
        by_type: byType,
        percentage_used: (total / TENANT_STORAGE_LIMIT) * 100,
        remaining_bytes: TENANT_STORAGE_LIMIT - total,
      };

      logger.info('Uso calculado', usageData);
      return usageData;
    },
    enabled: !!tenantId,
    staleTime: 10000, // 10 segundos
  });

  // =====================================================
  // MUTATION: Upload de arquivo
  // =====================================================
  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<MediaUploadResult> => {
      if (!tenantId) {
        throw new Error('Tenant não identificado');
      }

      logger.debug('Iniciando upload', { fileName: file.name, size: file.size });

      // 1. Validar arquivo
      const validation = validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // 2. Verificar espaço disponível
      const currentUsage = usage?.total_bytes || 0;
      if (currentUsage + file.size > TENANT_STORAGE_LIMIT) {
        const remaining = formatBytes(TENANT_STORAGE_LIMIT - currentUsage);
        throw new Error(
          `Limite de armazenamento excedido. Espaço disponível: ${remaining}`
        );
      }

      // 3. Gerar caminho de armazenamento com nome amigável
      // -----------------------------------------------------------------------
      // REGRA (v2.0.0):
      //   - O nome do arquivo no Storage deve preservar o nome original limpo
      //     (ex: "CARDAPIO_DELLA.pdf"), pois é o nome que o WhatsApp do contato
      //     receberá ao enviar a mídia via fluxos de automação.
      //   - Em caso de colisão (já existe arquivo com o mesmo nome no tenant
      //     para o mesmo tipo), adiciona sufixo de 2 dígitos APÓS o nome:
      //         CARDAPIO_DELLA.pdf  →  CARDAPIO_DELLA_01.pdf  →  _02.pdf ...
      //   - Após esgotar 01..99, fallback para sufixo aleatório de 2 chars.
      // -----------------------------------------------------------------------

      // Separa nome base e extensão (mantém apenas a última extensão)
      const lastDot = file.name.lastIndexOf('.');
      const rawBase = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
      const rawExt = lastDot > 0 ? file.name.substring(lastDot + 1) : '';

      // Sanitiza somente o nome base (preserva acentos comuns? não — mantém
      // padrão atual: apenas alfanuméricos, hífen e ponto)
      const sanitizedBase = rawBase.replace(/[^a-zA-Z0-9.-]/g, '_');
      const sanitizedExt = rawExt.toLowerCase().replace(/[^a-z0-9]/g, '');
      const baseDir = `MediaGallery/${tenantId}/${validation.file_type}`;

      // Helper: monta candidate path com sufixo opcional
      const buildPath = (suffix: string) => {
        const namePart = suffix ? `${sanitizedBase}_${suffix}` : sanitizedBase;
        return sanitizedExt
          ? `${baseDir}/${namePart}.${sanitizedExt}`
          : `${baseDir}/${namePart}`;
      };

      // Helper: verifica se já existe registro na tabela com esse storage_path
      const pathExists = async (candidate: string): Promise<boolean> => {
        const { data: existing } = await supabase
          .from('media_gallery')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('storage_path', candidate)
          .limit(1)
          .maybeSingle();
        return !!existing;
      };

      // Procura o primeiro caminho livre
      let storagePath = buildPath('');
      if (await pathExists(storagePath)) {
        let found = false;
        for (let i = 1; i <= 99; i++) {
          const suffix = i.toString().padStart(2, '0');
          const candidate = buildPath(suffix);
          if (!(await pathExists(candidate))) {
            storagePath = candidate;
            found = true;
            break;
          }
        }
        // Fallback: sufixo aleatório de 2 chars alfanuméricos minúsculos
        if (!found) {
          const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
          for (let attempt = 0; attempt < 10; attempt++) {
            const suffix =
              chars[Math.floor(Math.random() * chars.length)] +
              chars[Math.floor(Math.random() * chars.length)];
            const candidate = buildPath(suffix);
            if (!(await pathExists(candidate))) {
              storagePath = candidate;
              found = true;
              break;
            }
          }
          if (!found) {
            throw new Error(
              'Não foi possível gerar um nome único para o arquivo. Tente renomeá-lo.'
            );
          }
        }
      }

      logger.debug('Fazendo upload para Storage', { storagePath });

      // 4. Upload para Supabase Storage
      // Em caso de colisão real no Storage (corrida entre uploads simultâneos),
      // tenta até 3 vezes adicionando sufixo aleatório.
      let uploadError: { message?: string } | null = null;
      let uploadAttempts = 0;
      while (uploadAttempts < 3) {
        const { error } = await supabase.storage
          .from('anexos')
          .upload(storagePath, file, {
            contentType: file.type,
            upsert: false,
          });

        if (!error) {
          uploadError = null;
          break;
        }

        // Se for erro de duplicata, gera novo sufixo aleatório e tenta de novo
        const isDuplicate =
          error.message?.toLowerCase().includes('duplicate') ||
          error.message?.toLowerCase().includes('already exists');

        if (isDuplicate && uploadAttempts < 2) {
          const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
          const suffix =
            chars[Math.floor(Math.random() * chars.length)] +
            chars[Math.floor(Math.random() * chars.length)];
          storagePath = buildPath(suffix);
          uploadAttempts++;
          continue;
        }

        uploadError = error;
        break;
      }

      if (uploadError) {
        logger.error('Erro no upload', uploadError);
        throw new Error('Falha ao fazer upload do arquivo');
      }

      // 5. Extrair metadados
      const metadata = await extractMetadata(file, validation.file_type!);

      // 6. Registrar no banco
      const { data: mediaRecord, error: dbError } = await supabase
        .from('media_gallery')
        .insert({
          tenant_id: tenantId,
          name: file.name,
          file_type: validation.file_type!,
          mime_type: file.type,
          storage_path: storagePath,
          size_bytes: file.size,
          metadata: metadata,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (dbError) {
        logger.error('Erro ao registrar mídia no banco', dbError);
        // Tentar remover arquivo do storage em caso de erro
        await supabase.storage.from('anexos').remove([storagePath]);
        throw new Error('Falha ao registrar arquivo no banco de dados');
      }

      logger.info('Upload concluído', { id: mediaRecord.id });

      return {
        success: true,
        media: mediaRecord as MediaGalleryItem,
      };
    },
    onSuccess: (result) => {
      toast.success('Arquivo enviado com sucesso!');
      // Invalidar queries para atualizar UI
      queryClient.invalidateQueries({ queryKey: ['media-gallery', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['media-gallery-usage', tenantId] });
    },
    onError: (error: Error) => {
      logger.error('Erro no upload', error);
      toast.error(error.message || 'Erro ao fazer upload do arquivo');
    },
  });

  // =====================================================
  // MUTATION: Deletar arquivo
  // =====================================================
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      if (!tenantId) {
        throw new Error('Tenant não identificado');
      }

      logger.debug('Deletando mídia', { mediaId });

      // 1. Buscar informações do arquivo
      const { data: media, error: fetchError } = await supabase
        .from('media_gallery')
        .select('storage_path')
        .eq('id', mediaId)
        .single();

      if (fetchError || !media) {
        throw new Error('Arquivo não encontrado');
      }

      // 2. Remover do storage
      const { error: storageError } = await supabase.storage
        .from('anexos')
        .remove([media.storage_path]);

      if (storageError) {
        logger.warn('Erro ao remover do storage', storageError);
        // Continuar mesmo com erro no storage
      }

      // 3. Remover do banco
      const { error: dbError } = await supabase
        .from('media_gallery')
        .delete()
        .eq('id', mediaId);

      if (dbError) {
        logger.error('Erro ao deletar do banco', dbError);
        throw new Error('Falha ao excluir arquivo');
      }

      logger.info('Mídia deletada', { mediaId });
      return mediaId;
    },
    onSuccess: () => {
      toast.success('Arquivo excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['media-gallery', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['media-gallery-usage', tenantId] });
    },
    onError: (error: Error) => {
      logger.error('Erro ao deletar', error);
      toast.error(error.message || 'Erro ao excluir arquivo');
    },
  });

  // =====================================================
  // Função helper: Obter URL pública de um arquivo
  // =====================================================
  const getMediaUrl = (storagePath: string): string | undefined => {
    return getStorageUrl(storagePath);
  };

  return {
    // Dados
    mediaList,
    usage,
    
    // Estados de loading
    isLoadingMedia,
    isLoadingUsage,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Erros
    mediaError,
    
    // Operações
    uploadMedia: uploadMutation.mutate,
    uploadMediaAsync: uploadMutation.mutateAsync,
    deleteMedia: deleteMutation.mutate,
    deleteMediaAsync: deleteMutation.mutateAsync,
    refetchMedia,
    refetchUsage,
    getMediaUrl,
  };
}

// =====================================================
// FUNÇÕES AUXILIARES
// =====================================================

/**
 * Valida arquivo antes do upload
 */
function validateFile(file: File): FileValidationResult {
  // Verificar tipo MIME
  const fileType = getFileTypeFromMime(file.type);
  
  if (!fileType) {
    return {
      valid: false,
      error: `Tipo de arquivo não suportado: ${file.type}`,
    };
  }

  // Verificar tamanho
  if (!validateFileSize(fileType, file.size)) {
    const maxSize = formatBytes(MEDIA_SIZE_LIMITS[fileType]);
    return {
      valid: false,
      error: `Arquivo muito grande. Tamanho máximo para ${fileType}: ${maxSize}`,
    };
  }

  return {
    valid: true,
    file_type: fileType,
  };
}

/**
 * Extrai metadados do arquivo baseado no tipo
 */
async function extractMetadata(
  file: File,
  fileType: MediaFileType
): Promise<Record<string, any>> {
  const metadata: Record<string, any> = {
    original_filename: file.name,
    file_extension: file.name.split('.').pop() || '',
  };

  try {
    if (fileType === 'image') {
      // Extrair dimensões da imagem
      const dimensions = await getImageDimensions(file);
      metadata.width = dimensions.width;
      metadata.height = dimensions.height;
    } else if (fileType === 'video' || fileType === 'audio') {
      // Extrair duração de vídeo/áudio
      const duration = await getMediaDuration(file, fileType);
      metadata.duration = duration;
    }
  } catch (error) {
    logger.warn('Erro ao extrair metadados', error);
    // Continuar sem metadados
  }

  return metadata;
}

/**
 * Obtém dimensões de uma imagem
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Falha ao carregar imagem'));
    };

    img.src = url;
  });
}

/**
 * Obtém duração de vídeo ou áudio
 */
function getMediaDuration(file: File, type: 'video' | 'audio'): Promise<number> {
  return new Promise((resolve, reject) => {
    const element = type === 'video' 
      ? document.createElement('video') 
      : document.createElement('audio');
    
    const url = URL.createObjectURL(file);

    element.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(element.duration));
    };

    element.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Falha ao carregar ${type}`));
    };

    element.src = url;
  });
}
